import { memo, useCallback, useMemo } from 'react';

import { InfoIcon, MessageCircleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import ButtonCard from '@/components/atoms/Button/ButtonCard/ButtonCard';
import Bubble from '@/components/atoms/Icons/Bubble';
import BubbleSparkle from '@/components/atoms/Icons/BubbleSparkle';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type OptionItem =
  | { id: number; child: React.ReactNode; isButtonLike?: boolean; isTheme?: never; value: string }
  | { id: number; child: React.ReactNode; isButtonLike?: never; isTheme?: boolean; value: string };

type LauncherOptionsProps = {
  onChange: (value) => void;
  value: string;
  isDisabled?: boolean;
  disabled?: boolean;
};

const BUTTON_CARD_CLASSES = 'pointer-events-none px-2.5 py-2 [&>span]:inline [&_svg]:mr-1';

export const launcherTypeOptions = {
  '0': 'bubble-sparkle',
  '1': 'bubble',
  '2': 'help',
  '3': 'feedback',
} as const;

const LauncherOptions = ({
  onChange,
  value,
  isDisabled,
  disabled = false,
}: LauncherOptionsProps) => {
  const { t } = useTranslation();

  const items: OptionItem[] = useMemo(
    () => [
      { id: 0, child: <BubbleSparkle />, value: 'bubble-sparkle' },
      { id: 1, child: <Bubble />, value: 'bubble' },
      {
        id: 2,
        child: (
          <Button className={BUTTON_CARD_CLASSES}>
            <InfoIcon size={18} className="text-element-primary-foreground" />
            {t('integrations.web_launcher.types.help')}
          </Button>
        ),
        isButtonLike: true,
        value: 'help',
      },
      {
        id: 3,
        child: (
          <Button className={BUTTON_CARD_CLASSES}>
            <MessageCircleIcon className="text-element-primary-foreground" size={18} />
            {t('integrations.web_launcher.types.feedback')}
          </Button>
        ),
        isButtonLike: true,
        value: 'feedback',
      },
    ],
    [t]
  );

  const handleCardClick = useCallback(
    (index) => {
      if (!disabled) {
        onChange(launcherTypeOptions[index]);
      }
    },
    [disabled, onChange]
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs leading-3 font-semibold text-foreground uppercase">
        {t('integrations.web_launcher.options')}
      </div>
      <div
        className={cn(
          'flex items-center justify-start gap-5',
          disabled && 'cursor-not-allowed [&_div]:pointer-events-none'
        )}
      >
        {items.map((item) => {
          return (
            <ButtonCard
              active={value === item.value}
              onClick={handleCardClick}
              key={item.id}
              index={item.id}
              isTheme={item.isTheme}
              isButtonLike={item.isButtonLike}
              isDisabled={isDisabled}
            >
              {item.child}
            </ButtonCard>
          );
        })}
      </div>
    </div>
  );
};

export default memo(LauncherOptions);
